import React from 'react';
import { 
  Check, 
  Zap, 
  Star, 
  Crown, 
  ArrowRight,
  Code,
  Activity,
  Shield,
  Headphones
} from 'lucide-react';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: PlanFeature[];
  popular?: boolean;
  cta: string;
  icon: React.ReactNode;
  gradient: string;
}

const plans: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for testing and small projects',
    icon: <Code className="w-6 h-6" />,
    gradient: 'from-gray-500/20 to-gray-600/20',
    cta: 'Get Started',
    features: [
      { text: '100 API calls/day', included: true },
      { text: 'Basic price endpoints', included: true },
      { text: '15-minute data refresh', included: true },
      { text: 'Community support', included: true },
      { text: 'Real-time WebSocket feeds', included: false },
      { text: 'Historical data access', included: false },
      { text: 'Priority support', included: false },
      { text: 'Custom integrations', included: false }
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$29',
    period: 'month',
    description: 'For serious developers and growing applications',
    icon: <Zap className="w-6 h-6" />,
    gradient: 'from-blue-500/20 to-purple-500/20',
    popular: true,
    cta: 'Start Pro Trial',
    features: [
      { text: '10,000 API calls/day', included: true },
      { text: 'Real-time WebSocket feeds', included: true },
      { text: '1-minute data refresh', included: true },
      { text: 'Priority support', included: true },
      { text: 'Historical data access', included: true },
      { text: 'Webhook notifications', included: true },
      { text: 'Custom rate limits', included: false },
      { text: 'Dedicated endpoints', included: false }
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$199',
    period: 'month',
    description: 'For mission-critical applications with high volume',
    icon: <Crown className="w-6 h-6" />,
    gradient: 'from-orange-500/20 to-yellow-500/20',
    cta: 'Contact Sales',
    features: [
      { text: 'Unlimited API calls', included: true },
      { text: 'Dedicated endpoints', included: true },
      { text: 'Real-time streaming', included: true },
      { text: '99.9% SLA guarantees', included: true },
      { text: 'Custom integrations', included: true },
      { text: 'Phone support', included: true },
      { text: 'White-label options', included: true },
      { text: 'Custom data sources', included: true }
    ]
  }
];

export const SubscriptionPlans: React.FC = () => {
  return (
    <div className="bg-gray-900/10 backdrop-blur-sm rounded-2xl border border-gray-800/20 p-8">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 rounded-xl">
            <Star className="w-6 h-6 text-orange-400" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-300 bg-clip-text text-transparent">
            Oracle Subscription Plans
          </h2>
        </div>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Choose the perfect plan for your Oracle data needs. Scale from development to enterprise with confidence.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative group ${
              plan.popular ? 'scale-105 z-10' : ''
            }`}
          >
            {/* Popular Badge */}
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Most Popular
                </div>
              </div>
            )}

            {/* Plan Card */}
            <div className={`relative bg-gradient-to-br ${plan.gradient} backdrop-blur-sm rounded-2xl border ${
              plan.popular ? 'border-blue-500/40' : 'border-gray-800/30'
            } p-8 group-hover:border-gray-700/50 transition-all duration-300 h-full`}>
              
              {/* Plan Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900/40 rounded-2xl mb-4">
                  {plan.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1 mb-3">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-gray-400">/{plan.period}</span>
                </div>
                <p className="text-gray-400 text-sm">{plan.description}</p>
              </div>

              {/* Features List */}
              <div className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      feature.included 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-gray-700/30 text-gray-500'
                    }`}>
                      <Check className="w-3 h-3" />
                    </div>
                    <span className={`text-sm ${
                      feature.included ? 'text-gray-300' : 'text-gray-500 line-through'
                    }`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                plan.popular
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 shadow-lg hover:shadow-xl'
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-gray-700/50'
              }`}>
                {plan.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Info */}
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <div className="text-center p-4 bg-gray-900/20 rounded-xl border border-gray-800/30">
          <Activity className="w-8 h-8 text-blue-400 mx-auto mb-3" />
          <h4 className="font-semibold text-white mb-2">99.9% Uptime</h4>
          <p className="text-gray-400 text-sm">Enterprise-grade reliability with SLA guarantees</p>
        </div>
        <div className="text-center p-4 bg-gray-900/20 rounded-xl border border-gray-800/30">
          <Shield className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
          <h4 className="font-semibold text-white mb-2">Secure & Compliant</h4>
          <p className="text-gray-400 text-sm">SOC 2 compliant with enterprise security</p>
        </div>
        <div className="text-center p-4 bg-gray-900/20 rounded-xl border border-gray-800/30">
          <Headphones className="w-8 h-8 text-purple-400 mx-auto mb-3" />
          <h4 className="font-semibold text-white mb-2">24/7 Support</h4>
          <p className="text-gray-400 text-sm">Expert support when you need it most</p>
        </div>
      </div>
    </div>
  );
};
